import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { EASE, Reveal, HankoStamp } from './motion';
import { MATRIX, useLang, pick } from './content';

type MarkKind = 'full' | 'partial' | 'none' | 'underway';
interface Mark { v: MarkKind; label?: string }

const GLYPH: Record<MarkKind, string> = { full: '✓', partial: '△', none: '✗', underway: '◐' };
const COLOR: Record<MarkKind, string> = {
  full: 'text-emerald-700',
  partial: 'text-citation-600',
  none: 'text-ink-300',
  underway: 'text-citation-600',
};
const ARIA: Record<MarkKind, string> = { full: 'full', partial: 'partial', none: 'not available', underway: 'underway' };

function MarkCell({ mark, lang, strong }: { mark: Mark; lang: string; strong?: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center leading-tight">
      <span className={`${COLOR[mark.v]} ${strong ? 'text-[15px] font-bold' : 'text-[14px]'}`} aria-label={ARIA[mark.v]}>
        {GLYPH[mark.v]}
      </span>
      {mark.label && <span className="mt-0.5 text-[10px] text-ink-400">{pick(mark.label, lang)}</span>}
    </div>
  );
}

export function ComparisonMatrix() {
  const lang = useLang();
  const reduce = useReducedMotion();
  const cols = MATRIX.columns;

  const rowVariant: Variants | undefined = reduce ? undefined : { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } };

  return (
    <section className="border-t border-ink-100 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[12px] uppercase tracking-[0.18em] text-accent-600 font-semibold">{pick(MATRIX.eyebrow, lang)}</div>
            <h2 className="mt-2 display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 text-balance">
              {pick(MATRIX.h2, lang)}
            </h2>
          </div>
        </Reveal>

        {/* Desktop table */}
        <div className="hidden md:block mt-8 card-paper p-0 overflow-x-auto relative">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-paper-100 text-[11px] uppercase tracking-[0.12em] text-ink-500">
                <th scope="col" className="sticky left-0 bg-paper-100 text-left font-medium py-3 pl-5 pr-3 min-w-[230px]">
                  {pick('landing.matrix.capability', lang)}
                </th>
                {cols.map((c, i) => (
                  <th key={c} scope="col" className={`py-3 px-3 font-semibold text-center min-w-[110px] ${i === 0 ? 'text-accent-700 bg-accent-50/40' : 'text-ink-600'}`}>
                    <span className="inline-flex items-center gap-1.5 justify-center">
                      {i === 0 && <span className="brand-mark" style={{ height: 18, width: 18, fontSize: 11 }}>知</span>}
                      {c}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <motion.tbody
              initial={reduce ? undefined : 'hidden'}
              whileInView={reduce ? undefined : 'visible'}
              viewport={{ once: true, margin: '-40px' }}
              variants={reduce ? undefined : { hidden: {}, visible: { transition: { staggerChildren: 0.035 } } }}
            >
              {MATRIX.rows.map((row, ri) => (
                <motion.tr key={ri} variants={rowVariant} transition={{ duration: 0.35, ease: EASE }} className="border-t border-ink-50 hover:bg-paper-50">
                  <th scope="row" className="sticky left-0 bg-white text-left font-medium text-ink-800 py-2.5 pl-5 pr-3 text-[13px]">
                    {pick(row.cap, lang)}
                  </th>
                  {row.marks.map((m, ci) => (
                    <td key={ci} className={`py-2.5 px-3 ${ci === 0 ? 'bg-accent-50/40' : ''}`}>
                      <MarkCell mark={m as Mark} lang={lang} strong={ci === 0} />
                    </td>
                  ))}
                </motion.tr>
              ))}
            </motion.tbody>
          </table>
          <HankoStamp size={36} rotate={-10} className="absolute top-1.5 right-2" />
        </div>

        {/* Mobile: per-capability stacked cards */}
        <div className="md:hidden mt-8 space-y-2">
          {MATRIX.rows.map((row, ri) => (
            <Reveal key={ri} duration={0.4}>
              <div className="card-paper p-4">
                <div className="text-[13px] font-medium text-ink-800 mb-2">{pick(row.cap, lang)}</div>
                <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
                  {row.marks.map((m, ci) => (
                    <div key={ci} className={`flex items-center justify-between text-[12px] ${ci === 0 ? 'col-span-2 bg-accent-50/50 rounded-md px-2 py-1' : ''}`}>
                      <span className={ci === 0 ? 'text-accent-700 font-semibold' : 'text-ink-500'}>{cols[ci]}</span>
                      <span className="flex items-center gap-1">
                        <span className={COLOR[(m as Mark).v]} aria-label={ARIA[(m as Mark).v]}>{GLYPH[(m as Mark).v]}</span>
                        {(m as Mark).label && <span className="text-[10px] text-ink-400">{pick((m as Mark).label!, lang)}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-4 text-[12px]">
          <Link to="/compare" className="text-accent-600 hover:underline font-medium">
            {pick('landing.matrix.seeFull', lang)}
          </Link>
          <span className="text-ink-400">✓ {pick('landing.matrix.legend.full', lang)} · △ {pick('landing.matrix.legend.partial', lang)} · ◐ {pick('landing.matrix.legend.underway', lang)} · ✗ {pick('landing.matrix.legend.none', lang)}</span>
        </div>
      </div>
    </section>
  );
}
